import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';

import { InfoCircleFill as InfoCircleFillIcon } from '@styled-icons/bootstrap/InfoCircleFill';
import { Check as CheckIcon } from '@styled-icons/boxicons-regular/Check';
import { Warning as WarningIcon } from '@styled-icons/fluentui-system-regular/Warning';
import { motion, AnimatePresence } from 'framer-motion';
import InfoIcon from 'mdi-react/InfoCircleOutlineIcon';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Tooltip,
  Icon,
  Box,
  Progress,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import FileImportHelper from 'components/Modal/Import/FileImportHelper';
import {
  getAcceptedExtensionsLabel,
  importAccept,
} from 'components/Modal/Import/shared';

type FormErrors = {
  [key: string]: string | boolean;
  type: 'invalid' | 'required';
};

interface ModalBudgetImportProps extends ModalRootProps {
  data: {
    budgetId: number;
    budgetType?: 'sienge_plataform' | 'common';
  };
}

const baseStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 4,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  cursor: 'pointer',
  textAlign: 'center',
  fontSize: '14px',
};

const focusedStyle = {
  borderColor: '#2196f3',
};

const selectedStyle = {
  borderColor: '#2196f3',
  color: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
  color: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
  color: '#ff1744',
};

const MotionBox = motion.create(Box);

const ModalBudgetImport: React.FC<ModalBudgetImportProps> = ({
  onConfirm,
  handleClose,
  data: { budgetId, budgetType = 'common' },
  ...restProps
}) => {
  const [progress, setProgress] = useState(0);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({} as FormErrors);

  const [file, setFile] = React.useState<File | null>(null);
  const [headerLine, setHeaderLine] = React.useState<number | undefined>();

  const onDrop = useCallback((acceptedFiles) => {
    setFile(acceptedFiles[0]);
  }, []);

  const accept = importAccept;

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept,
    onDrop,
  });

  const handleConfirm = async (): Promise<void> => {
    const formErrors: FormErrors = {} as FormErrors;

    if (!headerLine) {
      formErrors.header = 'Linha do cabeçalho é obrigatório';
    }

    if (!file) {
      formErrors.file = 'Arquivo é obrigatório';
    }

    setErrors(formErrors);
    if (Object.keys(formErrors).length || !file) {
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('type', 'budget');
      formData.append('budget_id', String(budgetId));
      formData.append('header_row', String(headerLine));
      formData.append('description', String(file.name));
      formData.append('file', file);

      const {
        data: {
          id: importId,
          mapped_header: mappedHeaders,
          header_row: headerRow,
          file: importedFile,
        },
      } = await api
        .post(`/import`, formData, {
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total ?? 1;
            const uploadPercentage = Math.round(
              (progressEvent.loaded * 100) / total,
            );

            if (uploadPercentage < 100) {
              setProgress(uploadPercentage);
            }
          },
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => {
          setProgress(100);

          return response.data;
        });

      if (onConfirm)
        onConfirm({
          id: importId,
          mappedHeaders,
          headerRow,
          fileName: importedFile.name,
        });

      handleClose();
    } catch (err) {
      setProgress(0);
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro inesperado ao importar o arquivo.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(file ? selectedStyle : {}),
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(errors.file ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject, file, errors],
  );

  useEffect(() => {
    if (file && errors.file) {
      const newErrors = errors;
      delete newErrors.file;
      setErrors(newErrors);
    }
  }, [file, errors]);

  const extensions = getAcceptedExtensionsLabel(accept);

  const dropMessage = useMemo(() => {
    if (file) {
      return `Selecionado: ${file.name}`;
    }

    if (isDragAccept) {
      return 'Tudo certo! Pode soltar os arquivos aqui.';
    }

    if (isDragReject) {
      return `Tipo de arquivo não suportado. Tipos suportados: ${extensions}.`;
    }

    if (isDragActive) {
      return 'Solte os arquivos aqui...';
    }

    return 'Arraste ou clique para selecionar o arquivo do orçamento.';
  }, [file, extensions, isDragAccept, isDragReject, isDragActive]);

  return (
    <Modal size="3xl" scrollBehavior="inside" {...restProps}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Importar Excel / CSV da EAP</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={2} align="start">
            <HStack align="start">
              <Icon as={InfoIcon} color="blue.500" boxSize={4} mt={1} />
              <Text fontSize="sm">
                Após o envio do arquivo, você poderá indicar quais são as
                colunas de cada informação obrigatória.
              </Text>
            </HStack>

            <HStack align="start">
              <Icon as={InfoIcon} color="blue.500" boxSize={4} mt={1} />
              <Text fontSize="sm">
                Etapas e sub-etapas serão identificadas automaticamente quando
                não houver código de serviço associado.
              </Text>
            </HStack>

            <HStack align="start">
              <Icon as={InfoIcon} color="blue.500" boxSize={4} mt={1} />
              <Text fontSize="sm">
                A estrutura hierárquica (1, 1.1, 1.1.1...) será utilizada para
                reconhecer sub-etapas automaticamente.
              </Text>
            </HStack>

            {budgetType === 'sienge_plataform' && (
              <HStack align="start">
                <Icon as={WarningIcon} color="orange.500" boxSize={4} mt={1} />
                <Text fontSize="sm" fontWeight="bold">
                  Para importar Serviços a Preço Informado em orçamentos Sienge,
                  é necessário que o item esteja previamente cadastrado em sua
                  base própria de serviços.
                </Text>
              </HStack>
            )}
          </VStack>

          <Box mt={4}>
            <Text fontWeight="bold" mb={2}>
              Colunas necessárias
            </Text>

            <List spacing={2}>
              <ListItem fontSize="sm">
                <ListIcon as={CheckIcon} color="green.500" />
                Item (1, 1.1, 1.1.1...)
              </ListItem>

              <ListItem fontSize="sm">
                <ListIcon as={CheckIcon} color="green.500" />
                Tipo
              </ListItem>

              <ListItem fontSize="sm">
                <ListIcon as={CheckIcon} color="green.500" />
                Código
              </ListItem>

              <ListItem fontSize="sm">
                <ListIcon as={CheckIcon} color="green.500" />
                Base de dados
              </ListItem>

              <ListItem fontSize="sm">
                <ListIcon as={CheckIcon} color="green.500" />
                Descrição do serviço ou etapa
              </ListItem>

              <ListItem fontSize="sm">
                <ListIcon as={CheckIcon} color="green.500" />
                Coeficiente
              </ListItem>

              <ListItem fontSize="sm">
                <ListIcon as={CheckIcon} color="green.500" />
                Unidade de medida
              </ListItem>
            </List>
          </Box>

          <FormControl isInvalid={!!errors?.file} mt={4}>
            <FormLabel>Arquivo</FormLabel>

            <FileImportHelper accept={accept} type="budget" />

            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />

              <Text whiteSpace="normal" wordBreak="break-all" fontSize="sm">
                {dropMessage}
              </Text>
            </div>

            <FormErrorMessage>
              {!!errors?.file && 'Arquivo é obrigatório'}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.header} mt={4}>
            <FormLabel>
              Linha do cabeçalho{' '}
              <Tooltip
                ml={1}
                hasArrow
                label="Informe a em qual linha se encontra cabeçalho do orçamento no arquivo."
              >
                <Icon as={InfoCircleFillIcon} className="icon" />
              </Tooltip>
            </FormLabel>

            <Input
              type="number"
              value={headerLine}
              onChange={(e) => {
                if (e.target.value === '') {
                  setHeaderLine(undefined);
                  return;
                }

                const value = Number(e.target.value);

                if (value < 1) {
                  return;
                }

                setHeaderLine(value);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleConfirm();
                }
              }}
            />

            <FormErrorMessage>
              {!!errors?.header && 'Linha do cabeçalho é obrigatória'}
            </FormErrorMessage>
          </FormControl>

          <AnimatePresence>
            {progress > 0 && progress <= 100 ? (
              <MotionBox
                initial={{
                  opacity: 0,
                  scale: 0.4,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  transition: {
                    duration: 0.25,
                  },
                }}
                exit={{
                  opacity: 0,
                  top: -30,
                }}
                height="auto"
              >
                <Text mt={8}>
                  Enviando arquivo...
                  <Progress
                    mt={2}
                    value={progress}
                    hasStripe
                    isAnimated
                    colorScheme="blue"
                    borderRadius="8"
                  />
                </Text>
              </MotionBox>
            ) : null}
          </AnimatePresence>
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            isDisabled={loading}
            colorScheme="green"
            onClick={handleConfirm}
          >
            Próximo
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetImport;
